import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCheckCircle, FaTimesCircle, FaUser, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import '../styles/LabAdminDashboard.css';

const UserApprovals = ({ user }) => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [assetUserRequests, setAssetUserRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [rejectionReasonId, setRejectionReasonId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchPendingUsers();
    fetchAssetUserRequests();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        API_ENDPOINTS.LAB_ADMIN_PENDING_USERS,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setPendingUsers(data);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setError('Failed to fetch pending users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetUserRequests = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${API_ENDPOINTS.LAB_ADMIN_ASSET_USER_REQUESTS}?status=pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data?.data || (Array.isArray(response.data) ? response.data : []);
      setAssetUserRequests(data);
    } catch (err) {
      console.error('Error fetching asset user requests:', err);
    }
  };

  const handleApproval = async (userId, approve) => {
    try {
      const token = sessionStorage.getItem('token');
      const endpoint = approve
        ? `${API_ENDPOINTS.LAB_ADMIN_PENDING_USERS}/${userId}/approve`
        : `${API_ENDPOINTS.LAB_ADMIN_PENDING_USERS}/${userId}/reject`;

      await axios.patch(
        endpoint,
        { rejectionReason: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(approve ? 'User approved successfully!' : 'User rejected successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh the list
      fetchPendingUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Error processing approval');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleApproveAssetUserRequest = async (requestId) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${API_ENDPOINTS.LAB_ADMIN_ASSET_USER_REQUESTS}/${requestId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage('Asset user request approved!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchAssetUserRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'Error approving request');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="section-content">
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      </div>
    );
  }

  // Combine and merge both pending users and asset user requests
  const combinedRequests = [
    ...pendingUsers.map(u => ({
      ...u,
      type: 'staff',
      requestId: u._id,
      displayName: `${u.firstName} ${u.lastName}`,
      displayEmail: u.email
    })),
    ...assetUserRequests
      .filter(req => req.status === 'pending')
      .map(req => ({
        ...req,
        type: 'asset_user',
        requestId: req._id,
        displayName: `${req.firstName} ${req.lastName}`,
        displayEmail: req.email
      }))
  ];

  // Sort by most recent first
  const sortedRequests = combinedRequests.sort((a, b) => {
    const dateA = new Date(a.createdAt || a.requestDate || 0);
    const dateB = new Date(b.createdAt || b.requestDate || 0);
    return dateB - dateA;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = sortedRequests.slice(startIndex, endIndex);

  const handleApprovalWithType = (requestId, requestType, approve) => {
    if (requestType === 'staff') {
      handleApproval(requestId, approve);
    } else {
      if (approve) {
        handleApproveAssetUserRequest(requestId);
      } else {
        setRejectionReasonId(requestId);
      }
    }
  };

  return (
    <div className="section-content">
      <div className="section-header">
        <div>
          <h1>Pending User Approvals</h1>
          <p>Review and approve or reject new user requests</p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      {/* Unified Approvals Table */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', margin: 0, color: '#1f2937', fontWeight: '700' }}>
            All Pending Requests ({sortedRequests.length})
          </h2>
        </div>

        {sortedRequests.length > 0 ? (
          <>
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '25%' }}>Name</th>
                    <th style={{ width: '30%' }}>Email</th>
                    <th style={{ width: '15%' }}>Type</th>
                    <th style={{ width: '30%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRequests.map((req) => (
                    <tr key={req.requestId}>
                      <td style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {req.profilePic ? (
                          <img 
                            src={req.profilePic} 
                            alt={req.displayName}
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#e0f2ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#0052cc'
                          }}>
                            <FaUser size={14} />
                          </div>
                        )}
                        <span>{req.displayName}</span>
                      </td>
                      <td>{req.displayEmail}</td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: req.type === 'staff' ? '#dbeafe' : '#dcfce7',
                          color: req.type === 'staff' ? '#1e40af' : '#166534'
                        }}>
                          {req.type === 'staff' ? 'Staff' : 'Asset User'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-sm"
                          style={{
                            backgroundColor: '#10b981',
                            color: 'white',
                            padding: '8px 12px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            marginRight: '6px'
                          }}
                          onClick={() => handleApprovalWithType(req.requestId, req.type, true)}
                        >
                          <FaCheckCircle style={{ marginRight: '4px' }} /> Approve
                        </button>
                        {rejectionReasonId === req.requestId ? (
                          <>
                            <input
                              type="text"
                              value={rejectionReasonInput}
                              onChange={(e) => setRejectionReasonInput(e.target.value)}
                              placeholder="Rejection reason..."
                              style={{
                                padding: '6px 8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '13px',
                                marginRight: '6px',
                                width: '150px'
                              }}
                            />
                            <button
                              className="btn-sm"
                              style={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600',
                                marginRight: '6px'
                              }}
                              onClick={() => handleApprovalWithType(req.requestId, req.type, false)}
                            >
                              Submit
                            </button>
                            <button
                              onClick={() => {
                                setRejectionReasonId(null);
                                setRejectionReasonInput('');
                              }}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                backgroundColor: '#f3f4f6',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn-sm"
                            style={{
                              backgroundColor: '#ef4444',
                              color: 'white',
                              padding: '8px 12px',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                            onClick={() => setRejectionReasonId(req.requestId)}
                          >
                            <FaTimesCircle style={{ marginRight: '4px' }} /> Reject
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: currentPage === 1 ? '#f3f4f6' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: currentPage === 1 ? '#9ca3af' : '#374151'
                  }}
                >
                  <FaChevronLeft size={12} /> Previous
                </button>

                <div style={{ display: 'flex', gap: '4px' }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '4px',
                        border: page === currentPage ? 'none' : '1px solid #d1d5db',
                        backgroundColor: page === currentPage ? '#0052cc' : '#fff',
                        color: page === currentPage ? '#fff' : '#374151',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151'
                  }}
                >
                  Next <FaChevronRight size={12} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">
            <FaCheckCircle className="no-data-icon" />
            <p>No pending approval requests</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserApprovals;